import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '@/lib/config';
import { AuthFormCard } from '@/components/AuthFormCard';
import { AuthFormInput } from '@/components/AuthFormInput';
import './ActivateInvitation.css';

const ActivateInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const [formData, setFormData] = useState({ firstName: '', lastName: '', password: '', confirmPassword: '' });
  const [error, setError] = useState<string | null>(token ? null : 'Invalid invitation.');
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }

    const validateToken = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/validate-invitation?token=${encodeURIComponent(token)}`);
        if (!response.ok) {
          navigate('/');
          return;
        }
        const data: { requiresRegistration: boolean } = await response.json();
        if (!data.requiresRegistration) {
          navigate(`/confirm-class?token=${encodeURIComponent(token)}`);
        }
      } catch {
        navigate('/');
      }
    };

    validateToken();
  }, [token, navigate]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) return;
    if (!formData.firstName || !formData.lastName || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('The passwords do not match.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/complete-invitation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...formData }),
      });
      if (!response.ok) throw new Error(await response.text() || 'Account could not be created.');
      setCompleted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Account could not be created.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthFormCard title="Create account">
      {completed ? (
        <>
          <p>Your account has been created. You can now log in.</p>
          <Link className="auth-form-link" to="/">Back to login</Link>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="auth-form compact" autoComplete="off">
          <AuthFormInput
            id="firstName"
            name="firstName"
            type="text"
            value={formData.firstName}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="First name"
            required
            autoComplete="off"
          />

          <AuthFormInput
            id="lastName"
            name="lastName"
            type="text"
            value={formData.lastName}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="Last name"
            required
            autoComplete="off"
          />

          <AuthFormInput
            id="password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="Password"
            minLength={8}
            required
            autoComplete="off"
          />

          <AuthFormInput
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="Repeat password"
            minLength={8}
            required
            autoComplete="off"
          />

          {error && <div className="auth-form-error">{error}</div>}

          <button type="submit" className="auth-form-button" disabled={loading || !token}>
            {loading ? 'Creating...' : 'Create account'}
          </button>
        </form>
      )}
    </AuthFormCard>
  );
};

export default ActivateInvitation;
